import React from "react";
import { useLanguage } from "../../contexts/LanguageContext";
import { percentOf, previousTotalOf, rateChange, seriesChange } from "../../utils/analyticsSeries";
import { LIMITS } from "../../utils/analyticsHighlights";
import { capabilitiesOf, shownOnly } from "../../utils/analyticsCapabilities";
import { roundedShare } from "../../utils/analyticsSentences";
import RankedBars from "../charts/RankedBars";
import { formatCount, formatDecimal } from "../charts/format";
import { ActivityPlaceholder } from "./ActivityStates";
import BreakdownChart from "./BreakdownChart";
import CardsShown from "./CardsShown";
import ChoicesSection from "./ChoicesSection";
import EvolutionCard from "./EvolutionCard";
import KpiStrip from "./KpiStrip";
import Section from "./Section";
import StepFunnel from "./StepFunnel";
import ToolsUsed from "./ToolsUsed";

// O rotulo das encomendas leva o sufixo da variante (lookup ou faq), posto em `assistantMetrics`.
const ASSISTANT_METRICS = [
  { key: "product_cards_shown", label: "analyticsCardsShown", better: "up", needs: "cards" },
  { key: "card_clicks", label: "analyticsCardClicks", better: "up", needs: "cards" },
  { key: "add_to_cart_requests", label: "analyticsAddToCart", better: "up", needs: "cart" },
  { key: "campaign_searches", label: "analyticsCampaignSearches", better: "neutral", needs: "campaigns" },
  { key: "order_inquiries", label: "analyticsOrderInquiriesCount", better: "neutral", needs: "orders" },
];

const assistantMetrics = (capabilities) =>
  shownOnly(ASSISTANT_METRICS, capabilities).map((metric) =>
    metric.needs === "orders" ? { ...metric, label: `${metric.label}.${capabilities.orders_variant}` } : metric
  );

// A faixa mostra as taxas (por 100 cartoes e pesquisas sem resultado) e o funil mostra as contagens (E8).
// Os "por 100 cartoes" variam na diferenca, sem p.p.: nao se leem como percentagem. A taxa de pesquisas
// sem resultado fica a ambar com o mesmo limite e o mesmo minimo de pesquisas do destaque, para os dois
// dizerem o mesmo.
const AssistantStrip = ({ summary, previousComplete }) => {
  const { translate, language } = useLanguage();
  const { assistant, series } = summary;
  const capabilities = capabilitiesOf(summary);
  const m = series.metrics;
  const count = (value) => formatCount(value, language);
  const per100 = (value) => (value === null ? translate("analyticsNotAvailable") : formatDecimal(value, language));
  const searches = assistant.campaign_searches;
  const without = searches.values.false || 0;
  const share = roundedShare(without, searches.total);
  const needsAttention = share !== null && searches.total >= LIMITS.campaignSearches && share >= LIMITS.campaignShareWithoutResult;
  const kpis = [
    {
      key: "shown",
      needs: "cards",
      label: translate("analyticsCardsShown"),
      value: count(assistant.product_cards_shown),
      change: seriesChange({ series, key: "product_cards_shown", better: "up", previousComplete, language }),
      spark: [m.product_cards_shown.current, m.product_cards_shown.previous],
      context: translate("analyticsKpiPrevious", { previous: count(previousTotalOf(series, "product_cards_shown")) }),
    },
    {
      key: "clicks",
      needs: "cards",
      label: translate("analyticsClicksPer100"),
      value: per100(assistant.card_clicks_per_100_cards),
      change: rateChange({ series, part: "card_clicks", whole: "product_cards_shown", kind: "number", better: "up", previousComplete, language }),
      spark: [percentOf(m.card_clicks.current, m.product_cards_shown.current), percentOf(m.card_clicks.previous, m.product_cards_shown.previous)],
      context: translate("analyticsKpiClicks", { clicks: count(assistant.card_clicks), count: assistant.card_clicks }),
    },
    {
      key: "cart",
      needs: "cart",
      label: translate("analyticsCartPer100"),
      value: per100(assistant.add_to_cart_per_100_cards),
      change: rateChange({ series, part: "add_to_cart_requests", whole: "product_cards_shown", kind: "number", better: "up", previousComplete, language }),
      spark: [
        percentOf(m.add_to_cart_requests.current, m.product_cards_shown.current),
        percentOf(m.add_to_cart_requests.previous, m.product_cards_shown.previous),
      ],
      context: translate("analyticsKpiCartRequests", { requests: count(assistant.add_to_cart_requests), count: assistant.add_to_cart_requests }),
    },
    {
      key: "campaigns",
      needs: "campaigns",
      label: translate("analyticsCampaignsWithoutResult"),
      value: share === null ? translate("analyticsNotAvailable") : `${share}%`,
      change: rateChange({ series, part: "campaign_searches_without_result", whole: "campaign_searches", better: "down", previousComplete, language }),
      spark: [
        percentOf(m.campaign_searches_without_result.current, m.campaign_searches.current),
        percentOf(m.campaign_searches_without_result.previous, m.campaign_searches.previous),
      ],
      context: translate(searches.total === 1 ? "analyticsKpiCampaignSearchesOne" : "analyticsKpiCampaignSearches", { without: count(without), total: count(searches.total) }),
      attention: needsAttention,
    },
    {
      key: "orders",
      needs: "orders",
      label: translate(`analyticsOrderInquiriesCount.${capabilities.orders_variant}`),
      value: count(assistant.order_inquiries.total),
      change: seriesChange({ series, key: "order_inquiries", better: "neutral", previousComplete, language }),
      spark: [m.order_inquiries.current, m.order_inquiries.previous],
      context: translate("analyticsKpiPrevious", { previous: count(previousTotalOf(series, "order_inquiries")) }),
    },
  ];
  return <KpiStrip label={translate("analyticsHeadline")} kpis={shownOnly(kpis, capabilities)} />;
};

// Os cartoes como funil (com zero cartoes mostra zeros, de proposito): as contagens de cada passo e, em
// cada um, o "por 100 cartoes" que o backend manda.
// Sem carrinho o funil perde o passo e a nota dele.
const CardsFunnel = ({ assistant, cart }) => {
  const { translate, language } = useLanguage();
  const rate = (value) => (value === null ? null : formatDecimal(value, language));
  const rateLabel = translate("analyticsPer100CardsLabel");
  const steps = [
    { key: "shown", label: translate("analyticsCardsShown"), value: assistant.product_cards_shown },
    { key: "clicks", label: translate("analyticsCardClicks"), value: assistant.card_clicks, rate: rate(assistant.card_clicks_per_100_cards), rateLabel },
    ...(cart ? [{ key: "cart", label: translate("analyticsAddToCart"), value: assistant.add_to_cart_requests, rate: rate(assistant.add_to_cart_per_100_cards), rateLabel }] : []),
  ];
  return <StepFunnel title={translate("analyticsCardsTitle")} steps={steps} info={cart ? translate("analyticsAddToCartNote") : undefined} rateColumn={translate("analyticsPer100CardsColumn")} />;
};

// A frase das encomendas e das campanhas, feita dos dois numeros; sem pesquisas de campanha fica so a
// metade das encomendas. A parte sem resultado so se diz aqui, e nao outra vez no cartao das campanhas.
const ordersAndCampaignsKey = (orders, missed, variant) => {
  const lead = missed === null ? "analyticsOrdersLead" : "analyticsOrdersAndCampaignsLead";
  return `${lead}${orders === 1 ? "One" : ""}.${variant}`;
};

// O titulo e a frase da secao das encomendas e das campanhas, pelo que o chat tem: as duas metades, so as encomendas
// ou so as campanhas.
const ordersAndCampaignsSection = ({ capabilities, orders, missed, translate, language }) => {
  const ordersCount = formatCount(orders, language);
  if (!capabilities.campaigns) {
    return { title: "analyticsOrders", note: translate(ordersAndCampaignsKey(orders, null, capabilities.orders_variant), { orders: ordersCount }) };
  }
  if (!capabilities.orders) {
    return { title: "analyticsCampaigns", note: missed === null ? null : translate("analyticsCampaignsLead", { share: `${missed}%` }) };
  }
  return {
    title: "analyticsOrdersAndCampaigns",
    note: translate(ordersAndCampaignsKey(orders, missed, capabilities.orders_variant), { orders: ordersCount, share: `${missed}%` }),
  };
};

// O separador Assistente (spec do redesign, R7): o que o assistente fez. A faixa de numeros, os cartoes
// num funil, a evolucao, e as encomendas e as pesquisas de campanha, cada parte so com o que o chat tem:
// as encomendas e as campanhas ficam lado a lado quando o chat tem as duas, e uma sozinha ocupa a largura.
// O que o assistente fez (`activity`, de /analytics/assistant, D11 e D12) acrescenta os cartoes mais
// mostrados a seguir ao funil, as escolhas guiadas (`choices`, so quando o backend as manda) e as ferramentas
// usadas no fim; enquanto a resposta vem, ou se falhou, cada
// uma tem o seu esqueleto ou o seu erro, e o resto do separador desenha-se do resumo.
const AssistantTab = ({ summary, period, today, previousComplete, activity = null, activityError = null, onRetryActivity }) => {
  const { translate, language } = useLanguage();
  const { assistant } = summary;
  const capabilities = capabilitiesOf(summary);
  const searches = assistant.campaign_searches;
  const missed = roundedShare(searches.values.false || 0, searches.total);
  const orders = assistant.order_inquiries.total;
  const clicks = assistant.card_clicks_per_100_cards;
  const cartRate = assistant.add_to_cart_per_100_cards;
  const withCart = capabilities.cart;
  const metrics = assistantMetrics(capabilities);
  const bothOrdersAndCampaigns = capabilities.orders && capabilities.campaigns;
  let cardsNote = null;
  if (withCart && clicks !== null && cartRate !== null) {
    cardsNote = translate("analyticsCardsToCartLead", { clicks: formatDecimal(clicks, language), carts: formatDecimal(cartRate, language) });
  } else if (!withCart && clicks !== null) {
    cardsNote = translate("analyticsCardsLead", { clicks: formatDecimal(clicks, language) });
  }
  const ordersAndCampaigns = ordersAndCampaignsSection({ capabilities, orders, missed, translate, language });
  return (
    <div className="space-y-6">
      {metrics.length > 0 && <AssistantStrip summary={summary} previousComplete={previousComplete} />}
      {capabilities.cards && (
        <Section title={translate(withCart ? "analyticsCardsToCart" : "analyticsCardsAndClicks")} note={cardsNote} className="grid gap-4">
          <CardsFunnel assistant={assistant} cart={withCart} />
        </Section>
      )}
      {capabilities.cards && (activity ? <CardsShown activity={activity} cart={withCart} /> : <ActivityPlaceholder title={translate("analyticsTopCards")} error={activityError} onRetry={onRetryActivity} />)}
      {activity?.choices && <ChoicesSection choices={activity.choices} />}
      {metrics.length > 0 && (
        <Section title={translate("analyticsEvolution")} className="grid gap-4">
          <EvolutionCard
            series={summary.series}
            metrics={metrics}
            title={translate(summary.series.granularity === "hour" ? "analyticsHourByHour" : "analyticsDayByDay")}
            partialLast={period.to === today}
            previousComplete={previousComplete}
            cut={summary.cut}
            timeZone={period.tz}
          />
        </Section>
      )}
      {(capabilities.orders || capabilities.campaigns) && (
        <Section title={translate(ordersAndCampaigns.title)} note={ordersAndCampaigns.note} className={bothOrdersAndCampaigns ? "grid gap-4 lg:grid-cols-2 items-start" : "grid gap-4"}>
          {capabilities.orders && <RankedBars title={translate(`analyticsOrderInquiries.${capabilities.orders_variant}`)} breakdown={assistant.order_inquiries} dimension="order_outcome" />}
          {capabilities.campaigns && (
            <BreakdownChart
              title={translate("analyticsCampaignSearches")}
              info={translate("analyticsCampaignSearchesNote")}
              breakdown={searches}
              dimension="matched"
            />
          )}
        </Section>
      )}
      {activity ? (
        <ToolsUsed tools={activity.tools} ordersVariant={capabilities.orders_variant} />
      ) : (
        <ActivityPlaceholder title={translate("analyticsToolsUsed")} error={activityError} onRetry={onRetryActivity} />
      )}
    </div>
  );
};

export default AssistantTab;
